<template>
    <div class="create-opportunity-process-modal">
        <ProcessModal
            data-qa="create-opportunity-modal-process"
            :is-open="isOpen"
            :title="$t('funnel.edit.campaign.create.opportunity.title')"
            :summary-text="$t('funnel.edit.campaign.create.opportunity.summary')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <div class="grid-container">
                    <div class="wrap-title">
                        <DsInputField
                            v-model="opportunity.title"
                            :label="$t('funnel.edit.campaign.create.opportunity.placeholder.title')"
                        />
                        <DsPopover position="left">
                            <template #reference>
                                <DsIconButton
                                    class="tooltip-icon"
                                    data-qa="tooltip-button"
                                    name="alert-circle"
                                />
                            </template>
                            <template #default>
                                <div class="popover-content">
                                    {{ $t('funnel.edit.campaign.create.opportunity.tooltip.left.blank') }}
                                </div>
                            </template>
                        </DsPopover>
                    </div>

                    <DsMultiselect
                        v-model="opportunity.startingStage"
                        bind-value-only
                        required
                        :force-invalid="emptyStage"
                        class="starting-stage-select"
                        data-qa="starting-stage-select"
                        :placeholder="$t('funnel.edit.campaign.create.opportunity.placeholder.starting.stage')"
                        :options="startingStages"
                    >
                        <template v-if="emptyStage" #help>
                            <span data-qa="empty-stage">{{ $t('funnel.edit.campaign.create.opportunity.required') }}</span>
                        </template>
                    </DsMultiselect>
                    <div class="wrap-round-robin">
                        <DsMultiselect
                            v-model="opportunity.assignToUser"
                            bind-value-only
                            required
                            class="assign-user-select"
                            data-qa="assign-user-select"
                            :placeholder="$t('funnel.edit.campaign.create.opportunity.placeholder.assign.user')"
                            :options="assignToUsers"
                            :submitted="emptyUser"
                            @input="disableRoundRobinDropdown"
                        >
                            <template v-if="emptyUser" #help>
                                <span data-qa="empty-user">{{ $t('funnel.edit.campaign.create.opportunity.required') }}</span>
                            </template>
                        </DsMultiselect>
                        <DsMultiselect
                            v-if="enableRoundRobinDropdown"
                            v-model="opportunity.roundRobinType"
                            bind-value-only
                            required
                            class="round-robin-select"
                            data-qa="round-robin-select"
                            :placeholder="$t('funnel.edit.campaign.create.opportunity.placeholder.round.robin.type')"
                            :options="roundRobinTypes"
                            :force-invalid="emptyRoundRobin"
                        >
                            <template v-if="emptyRoundRobin" #help>
                                <span data-qa="empty-round-robin">{{ $t('funnel.edit.campaign.create.opportunity.required') }}</span>
                            </template>
                        </DsMultiselect>
                    </div>
                    <DsMultiselect
                        v-model="opportunity.interestBundle"
                        bind-value-only
                        data-qa="interest-bundle-select"
                        class="interest-bundle-select"
                        :placeholder="$t('funnel.edit.campaign.create.opportunity.placeholder.interest.bundle')"
                        :options="bundleOptions"
                    />
                    <DsCheckbox
                        v-model="opportunity.onlyIfNoActive"
                        class="checkbox-not-active"
                        :label="$t('funnel.edit.campaign.create.opportunity.checkbox.onlyIfNoActive')"
                    />
                    <div class="section-separator" />
                    <div class="half-content">
                        <span class="next-action-title">{{ $t('funnel.edit.campaign.create.opportunity.next.action.date') }}</span>
                        <div class="next-action-input">
                            <DsInputField
                                v-model="opportunity.nextActionWaitDays"
                                type="number"
                                :min="0"
                                :max="9999"
                                :step="1"
                                :label="$t('funnel.edit.campaign.create.opportunity.next.action.date.days')"
                            />
                            <span class="date-from-label"> {{ $t('funnel.edit.campaign.create.opportunity.next.action.date.days.from.created') }} </span>
                        </div>
                    </div>
                    <DsTextAreaField
                        v-model="opportunity.nextActionNotes"
                        class="next-action-notes"
                        :label="$t('funnel.edit.campaign.create.opportunity.placeholder.next.action')"
                    />
                </div>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import {
    CREATE_OPPORTUNITY_USE_ROUND_ROBIN,
} from '../../../../utils/editor.constants';
import {
    getOpportunityConfig,
} from './create.opportunity.api';
import { utils } from '../../../../utils/legacy-utils';

export default {
    components: { ProcessModal },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            opportunity: {
                title: '',
                startingStage: undefined,
                assignToUser: undefined,
                roundRobinType: undefined,
                interestBundle: undefined,
                onlyIfNoActive: false,
                nextActionWaitDays: 1,
                nextActionNotes: undefined,
            },
            startingStages: [],
            assignToUsers: [],
            roundRobinTypes: [],
            bundleOptions: [],
            enableRoundRobinDropdown: false,
            emptyStage: false,
            emptyUser: false,
            emptyRoundRobin: false,
        };
    },

    async mounted() {
        await this.loadDropdowns();

        if (this.cell?.value?.stageId) {
            this.loadValues();
        }
    },

    methods: {
        loadValues() {
            this.opportunity.title = this.cell?.value?.opportunityTitle;
            this.opportunity.startingStage = parseInt(this.validateIfEmpty(this.cell?.value?.stageId), 10);
            this.opportunity.assignToUser = utils.removeLongSuffix(this.cell?.value?.userId.toString());
            this.opportunity.roundRobinType = this.validateIfEmpty(this.cell?.value?.roundRobinId);
            this.opportunity.interestBundle = this.validateIfEmpty(this.cell?.value?.bundleId);
            this.opportunity.onlyIfNoActive = this.cell?.value?.onlyIfNoActive === 1;
            this.opportunity.nextActionWaitDays = this.validateIfEmpty(this.cell?.value?.nextActionWaitDays, false);
            this.opportunity.nextActionNotes = this.cell?.value?.nextActionNotes;
            this.disableRoundRobinDropdown();
        },

        sortUserAssignedOptions() {
            const fixedOptions = ['0', '-1'];

            this.assignToUsers.sort((a, b) => {
                const indexA = fixedOptions.indexOf(a.value);
                const indexB = fixedOptions.indexOf(b.value);

                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                }

                if (indexA !== -1) {
                    return -1;
                }

                if (indexB !== -1) {
                    return 1;
                }

                return a.label.localeCompare(b.label);
            });
        },

        validateIfEmpty(attribute, removeLongSuffix = true) {
            if (removeLongSuffix) {
                return attribute && attribute !== 0 ? utils.removeLongSuffix(attribute).toString() : undefined;
            }

            return attribute && attribute !== 0 ? attribute : undefined;
        },

        disableRoundRobinDropdown() {
            if (this.opportunity.assignToUser === CREATE_OPPORTUNITY_USE_ROUND_ROBIN) {
                this.enableRoundRobinDropdown = true;
            } else if (this.opportunity.assignToUser) {
                this.opportunity.roundRobinType = undefined;
                this.enableRoundRobinDropdown = false;
            }
        },

        closeModal() {
            this.$emit('close-modal');
        },

        async loadDropdowns() {
            const opportunityConfigDto = await getOpportunityConfig();

            this.roundRobinTypes = this.fromObjectToArray(opportunityConfigDto.roundRobinMap);
            this.startingStages = opportunityConfigDto.stageList;
            this.assignToUsers = this.fromObjectToArray(opportunityConfigDto.userMap);
            this.bundleOptions = this.fromObjectToArray(opportunityConfigDto.bundleMap);
            this.sortUserAssignedOptions();
        },

        fromObjectToArray(object) {
            return Object.entries(object).map(([value, label]) => ({
                value,
                label,
            }));
        },

        async updateConfig() {
            if (this.isValid()) {
                const { opportunity, cell } = this;

                cell.value = cell.value || {};

                cell.value.opportunityTitle = opportunity.title;
                cell.value.stageId = this.validateAttribute(opportunity.startingStage);
                cell.value.userId = this.validateAttribute(opportunity.assignToUser);
                cell.value.onlyIfNoActive = opportunity.onlyIfNoActive ? 1 : 0;
                cell.value.nextActionWaitDays = this.validateAttribute(opportunity.nextActionWaitDays, false);
                cell.value.roundRobinId = this.validateAttribute(opportunity.roundRobinType) ?? 0;
                cell.value.bundleId = this.validateAttribute(opportunity.interestBundle) ?? 0;
                cell.value.nextActionNotes = opportunity.nextActionNotes ?? '';

                this.$emit('update-cell-value', { cell });
            }
        },

        validateAttribute(attribute, addLongSuffix = true) {
            utils.useZeroIfUndefined(attribute);

            if (attribute !== 0 && addLongSuffix) {
                utils.ensureLongEndsWithL(attribute);
            } else {
                Number(attribute);
            }

            return attribute;
        },

        isEmptyStage() {
            this.emptyStage = !this.opportunity.startingStage;
        },

        isEmptyUser() {
            this.emptyUser = !this.opportunity.assignToUser;
        },

        isEmptyRoundRobin() {
            if (this.opportunity.assignToUser === CREATE_OPPORTUNITY_USE_ROUND_ROBIN) {
                this.emptyRoundRobin = !this.opportunity.roundRobinType;
            } else {
                this.emptyRoundRobin = false;
            }
        },

        isValid() {
            this.isEmptyUser();
            this.isEmptyStage();
            this.isEmptyRoundRobin();

            return !this.emptyUser && !this.emptyStage && !this.emptyRoundRobin;
        },
    },
};
</script>

<style lang="scss" scoped>
    .checkbox-not-active {
        padding-top: 0;
        padding-bottom: $spacing-300;
        padding-left: 0;
    }
    .grid-container {
        display: grid;
    }
    .wrap-title {
        grid-template-columns: 95% 5%;
    }

    .wrap-title {
        display: grid;
        grid-template-columns: 90% 10%;
    }

    .wrap-round-robin {
        display: grid;
        gap: px-to-rem(16px);
        grid-template-columns: 1fr 1fr;
    }

    .half-content {
        display: grid;
        gap: px-to-rem(16px);
        width: 50%;
    }

    .tooltip-icon {
        margin-left: px-to-rem(12px);
        height: px-to-rem(40px);
        display: block;
    }
    .section-separator {
        border-top: px-to-rem(2px) solid #{$color-gray-200};
    }
    .next-action-title {
        margin-top: $spacing-300;
        font-weight: bold;
        font-size: $font-size-sm;
    }
    .next-action-input {
        display: grid;
        grid-template-columns: 35% 1fr;
        gap: px-to-rem(12px);
        align-items: center;
    }

    .date-from-label {
        margin-bottom: $spacing-300;
    }

    .next-action-notes {
        margin: 0;
    }
</style>
